/**
 * Status **interface** sections for the Entity Details panel — the middle level
 * of the component → interface → connection hierarchy (ADR-024 §4 amended
 * 2026-07-31; REQ-STATUS-039; feature-spec §2.2).
 *
 * An interface is one icon in an ``Ingress`` / ``Egress`` node's stack. Selecting
 * it renders **interface-level fields plus a roster of that interface's own
 * connections**, each row expandable in place to a drawer carrying that
 * connection's detail. The drawer is a disclosure *within* this pane, so the
 * interface remains the single inspected entity on the bus and ADR-024 §5's
 * one-focus contract is unchanged.
 *
 * # Two sources, joined on the interface name
 *
 * - the **config roster** (``GET /api/admin/peers/interfaces``) is
 *   authoritative for *existence* — an interface the operator just created is
 *   here immediately;
 * - the **status feed** is authoritative for *health*, and a direct egress
 *   interface reports with ``component_id == config.name``
 *   (:func:`interfaceComponentId`).
 *
 * The join is a **left** join from config onto status: an interface that exists
 * but has not yet reported is shown as honestly ``unknown``, never as absent and
 * never as green (REQ-STATUS-038 / REQ-STATUS-016). The same identity is what
 * lets the roster reuse the per-component route (REQ-STATUS-021) with **no API
 * change** — and it is exactly the scoping the pre-hierarchy code got wrong by
 * taking the first component of a kind.
 *
 * A **CDS** interface reaches this pane like any other (architect, 2026-08-01 —
 * a CDS Egress is an ordinary pre-configured peer interface). It has no
 * reporting component *yet*: `meridian_peer_manager` does not emit per-interface
 * status for the CDS types, which is issue #754, not a display rule. The
 * no-reporting-component branch below therefore is not a defensive edge case —
 * it is the live path for every CDS interface until that lands, and it must read
 * as "configured, health not reported" rather than as a fault.
 *
 * # Metric labels are inherited, not re-derived
 *
 * The vitals block below is :func:`ComponentVitalsSections`, which resolves its
 * resource and domain row labels from the deployment's metric-presentation
 * catalog by the pair ``(component_kind, metric_key)`` (REQ-STATUS-050 / -053).
 * An interface therefore labels its metrics under **its own reporting
 * component's** kind — ``sapient_egress`` reads differently from ``bff`` for the
 * same ``connections_active`` key, which is the whole point of keying on the
 * pair. There is deliberately no catalog fetch of its own here: one cached query
 * serves both panes.
 *
 * Cross-references:
 *   - REQ-STATUS-039 (interface selection + roster), -044 (roster + drawer),
 *     -038 (left join / never a false green), -021 (per-component route).
 *   - REQ-STATUS-050 / -053 / -054 (metric catalog, pair keying, raw-key fallback).
 *   - ADR-024 §4/§5; REQ-UI-046 (status palette); REQ-UI-059 (keyboard).
 */
import { CaretLeft } from "@phosphor-icons/react";
import {
  parseRollupState,
  type ResolvedComponent,
  type RollupState,
} from "@/system-status/status-model";
import {
  connectionsSummaryState,
  connectionsSummaryText,
  CDS_PEER_INTERFACE_TYPE_IDS,
  type ConfiguredInterface,
} from "@/system-status/status-topology";
import { MiniStateRow, StatusSectionsProvider } from "@/system-status/status-detail-primitives";
import { SectionShell, SectionRow } from "@/entity-details/section-shell";
import {
  ComponentVitalsSections,
  ConnectionRosterSection,
} from "@/entity-details/status-component-sections";
import { StatusTrendSection } from "@/entity-details/status-trend-section";
import {
  SPOOL_METRIC_KEYS,
  interfaceComponentId,
  resolveSpooled,
} from "@/entity-details/status-detail-scoping";

/**
 * Whether this interface is one the peer manager **never** reports per-interface.
 *
 * The distinction this draws is the whole point (feature-spec §9 decision 5,
 * architect 2026-08-02). Both a CDS interface and a silent direct interface land
 * on ``component === undefined``, but they are not the same situation:
 *
 *   - a **direct** interface that is not reporting is something to look into;
 *   - a **CDS** interface is not reporting *by design* — ``meridian_peer_manager``
 *     emits no per-interface ``ComponentStatusReport`` for these types at all
 *     (issue #754).
 *
 * Softening the first into the second would hide a real fault; hardening the
 * second into the first cries wolf on the highest-consequence egress path every
 * time an operator opens it. So they read differently.
 *
 * This is the first branch on :data:`CDS_PEER_INTERFACE_TYPE_IDS`, whose
 * comment previously recorded that nothing branched on it. It is a **display**
 * rule about what is honest to claim, not a status rule: nothing here changes a
 * rollup, and when #754 lands and these interfaces report, the set empties and
 * this branch stops firing on its own.
 */
function isCdsInterface(configured: ConfiguredInterface | null): boolean {
  return configured !== null && CDS_PEER_INTERFACE_TYPE_IDS.has(configured.typeId);
}

/** Props for :func:`StatusInterfaceSections`. */
export interface StatusInterfaceSectionsProps {
  /** The interface's operator label — the config ⋈ status join key. */
  interfaceName: string;
  /** The configured interface, or null when the config roster does not list it. */
  configured: ConfiguredInterface | null;
  /** The interface's reporting component, or undefined when it has not reported. */
  component: ResolvedComponent | undefined;
  /** Display name of the hosting node, for the back breadcrumb (Flow L §9). */
  parentLabel: string;
  /** Step the selection up to the hosting ``status-component``. */
  onBack: () => void;
}

/**
 * Render the interface detail body: a back breadcrumb to the hosting node, the
 * interface's identity + connection roll-up, its vitals (when a component
 * reports it), and its own connection roster with per-row drawers.
 */
export function StatusInterfaceSections({
  interfaceName,
  configured,
  component,
  parentLabel,
  onBack,
}: StatusInterfaceSectionsProps): React.ReactElement {
  // Left join: unreported means "not yet reporting", not "healthy".
  const state: RollupState =
    component === undefined ? "unknown" : parseRollupState(component.rollup_state);
  // The fields the retired connection roll-up node presented for this interface
  // (REQ-STATUS-039) — the same derivation the node used, so the roll-up line
  // here and the icon's dot can never tell different stories. A standby parent
  // forces standby so a stopped interface's honest 0/0/0 is not painted green
  // (ADR-036 §D-7).
  const isCds = isCdsInterface(configured);
  const summaryState = connectionsSummaryState(
    component?.connections_total,
    component?.connections_active,
    component?.connections_down,
    state,
  );
  // REQ-UI-149. `null` means this session cannot tell, which is said rather than
  // resolved either way — see `resolveSpooled`.
  const spooled = resolveSpooled(configured);
  const hiddenDomainKeys = spooled === true ? [] : SPOOL_METRIC_KEYS;
  const hiddenDomainNote =
    spooled === false
      ? "This interface delivers directly, so it has no spool. Spool depth and oldest-envelope age are omitted rather than shown as zero."
      : spooled === null
        ? "Spool depth and oldest-envelope age are not shown: this session cannot see whether this interface runs a spool, and a zero here would read as an empty one."
        : undefined;

  const summaryText = connectionsSummaryText(
    component?.connections_total,
    component?.connections_active,
    component?.connections_down,
    state,
  );

  return (
    // Session-local collapse for this pane's sections (REQ-UI-131) — the same
    // store the connection drawers in its roster inherit.
    <StatusSectionsProvider>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <button
          type="button"
          onClick={onBack}
          data-testid="interface-back"
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-meta text-text-secondary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          <CaretLeft size={12} weight="bold" aria-hidden="true" />
          Back to {parentLabel}
        </button>
      </div>

      <SectionShell id="interface-identity" title="Interface">
        <SectionRow label="Name" value={<span className="font-mono">{interfaceName}</span>} />
        <SectionRow
          label="Type"
          value={
            configured === null ? (
              // Reporting but not configured: the reverse of the left join, and
              // worth saying out loud rather than blanking — it means the config
              // roster and the feed disagree about what exists.
              <span data-testid="interface-unconfigured" className="text-text-muted">
                not in the configuration roster
              </span>
            ) : (
              <span className="font-mono">{configured.typeId}</span>
            )
          }
        />
        <SectionRow
          label="Reports as"
          value={
            isCds ? (
              // Not "not yet" — there is no per-interface report coming for a CDS
              // type, so a wording that implies one is late is a claim the system
              // cannot keep (issue #754).
              <span data-testid="interface-not-individually-reported" className="text-text-muted">
                not individually reported — this interface type has no per-interface status
              </span>
            ) : component === undefined ? (
              <span data-testid="interface-not-reporting" className="text-text-muted">
                not yet reporting
              </span>
            ) : (
              <span className="font-mono">{interfaceComponentId(interfaceName)}</span>
            )
          }
        />
      </SectionShell>

      <SectionShell id="interface-connections-summary" title="Connection roll-up">
        <MiniStateRow
          testId="interface-connections-summary"
          label="Connections"
          state={summaryState}
          detail={summaryText}
        />
      </SectionShell>

      {isCds ? (
        // Stated, not blank. An empty pane here reads either as "broken" or as
        // "no traffic", and on the CDS path the second is a false green
        // (REQ-STATUS-016) — the same rule REQ-STATUS-073 already applies to a
        // series absent for a known reason: an absence with a cause is said.
        <p
          className="px-4 py-3 text-body-sm text-text-muted"
          data-testid="interface-cds-not-reported"
        >
          Not individually reported — the peer manager publishes no per-interface status for CDS
          interface types, so this interface has no health, resource, connection or trend detail of
          its own. This is by design today, not a fault and not an absence of traffic; the flow
          itself is covered by the Peer Manager component&rsquo;s own status.
        </p>
      ) : component === undefined ? (
        <p className="px-4 py-3 text-body-sm text-text-muted" data-testid="interface-unknown">
          Not collected — the status feed reports no component for this interface, so its health,
          resource and connection detail are unknown (never a false green).
        </p>
      ) : (
        <>
          <ComponentVitalsSections
            component={component}
            nodeState={state}
            hiddenDomainKeys={hiddenDomainKeys}
            hiddenDomainNote={hiddenDomainNote}
          />
          {/* REQ-STATUS-064 names component, interface AND connection. The other
              two panes carried a Trend section from the start; this one did not,
              so an operator inspecting a direct peer interface saw its vitals
              with no history beside them. */}
          <StatusTrendSection
            componentId={component.component_id}
            componentKind={component.component_kind}
            entityLabel={interfaceName}
            domainOrder={Object.keys(component.domain ?? {})}
          />
          <ConnectionRosterSection componentId={interfaceComponentId(interfaceName)} />
        </>
      )}
    </StatusSectionsProvider>
  );
}
